/**
 * Role-Based Access Guard
 */

import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { UserRole } from '@/types/auth.types'

const roleHierarchy: Record<string, number> = {
    [UserRole.MEMBER]: 1,
    [UserRole.CHAPTER_LEAD]: 1.5,
    [UserRole.CURATOR]: 2,
    [UserRole.ADMINISTRATOR]: 3,
}

export function roleGuard(requiredRole: UserRole) {
    return async (
        to: RouteLocationNormalized,
        _from: RouteLocationNormalized
    ) => {
        const authStore = useAuthStore()

        if (!authStore.isAuthenticated) {
            return { name: 'login', query: { redirect: to.fullPath } }
        }

        // Wait for profile to load if it hasn't yet
        if (authStore.isAuthenticated && !authStore.profile) {
            await authStore.fetchProfile()
        }

        const userRoleLevels = authStore.userRoles.map((r: string | number) => roleHierarchy[r as string] || 0)
        const maxUserRoleLevel = Math.max(0, ...userRoleLevels)
        const requiredRoleLevel = roleHierarchy[requiredRole] || 0

        if (maxUserRoleLevel >= requiredRoleLevel) {
            return true
        }
        // Insufficient permissions
        return { name: 'home' }
    }
}

export function adminGuard(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized
) {
    return roleGuard(UserRole.ADMINISTRATOR)(to, _from)
}

export function curatorGuard(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized
) {
    return roleGuard(UserRole.CURATOR)(to, _from)
}
